import { useState, useEffect } from "react";
import axios from "axios";
import { Sparkles, ArrowRight, Target, Brain, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";

export const Hero = () => {
  const [signupCount, setSignupCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await axios.get(`${API}/prelaunch/count`);
        setSignupCount(response.data.count);
      } catch {
        // Silently handle error - count will remain 0
      }
    };
    fetchCount();
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-cyan-50/30 to-indigo-50/30" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(6,182,212,0.1),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(99,102,241,0.05),transparent_50%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7 space-y-6 md:space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-100 border border-cyan-200">
              <Sparkles className="w-4 h-4 text-cyan-600" />
              <span className="text-cyan-700 text-sm font-medium tracking-wide">
                Patented Technology
              </span>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
              <span className="text-slate-800">Test Your </span>
              <span className="gradient-text">Kidney Health</span>
              <span className="text-slate-800"> at Home in</span>
              <span className="block text-cyan-600 mt-2">90 Seconds</span>
            </h1>

            <p className="text-lg md:text-xl text-slate-600 max-w-xl leading-relaxed">
              India's first AI + Computer Vision platform that turns your smartphone into a kidney lab — lab-accurate results from just 2 drops of urine in 90 seconds.
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <a href="#signup" className="w-full sm:w-auto">
                <Button
                  data-testid="hero-cta-primary"
                  size="lg"
                  className="w-full sm:w-auto bg-cyan-500 hover:bg-cyan-600 text-slate-800 font-semibold px-8 py-6 text-lg group"
                >
                  Join Pre-Launch
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <a href="#how-it-works" className="w-full sm:w-auto">
                <Button
                  data-testid="hero-cta-secondary"
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto border-slate-300 text-slate-700 hover:bg-slate-100 px-8 py-6 text-lg"
                >
                  See How It Works
                </Button>
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-3 sm:gap-6 pt-2 sm:pt-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                </div>
                <span className="text-slate-700 font-semibold text-sm sm:text-base whitespace-nowrap">95% Accuracy</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600" />
                </div>
                <span className="text-slate-700 font-semibold text-sm sm:text-base whitespace-nowrap">AI/ML Powered</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                </div>
                <span className="text-slate-700 font-semibold text-sm sm:text-base whitespace-nowrap">{signupCount > 0 ? `${signupCount}+ Signups` : "Pre-Launch"}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative flex justify-center">
            <div className="relative animate-float">
              <div className="absolute inset-0 bg-cyan-200/20 rounded-3xl blur-3xl scale-110" />

              <div className="relative">
                <div className="w-full mx-auto relative flex items-center justify-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_1488b547-0470-4710-9082-0fb679cd81f3/artifacts/fgwli7y0_WhatsApp%20Image%202026-05-20%20at%2011.23.17.jpeg"
                    alt="KidneyCheQ - Instant Kidney Health Detection Kit"
                    className="max-h-[320px] w-auto rounded-2xl shadow-2xl -rotate-90 drop-shadow-xl"
                    data-testid="kidneycheq-product-image"
                  />
                </div>

                <div className="flex justify-center gap-3 sm:gap-6 -mt-2">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-cyan-100">
                    <p className="text-2xl font-bold text-cyan-600">90s</p>
                    <p className="text-xs text-slate-500">Results</p>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-emerald-100">
                    <p className="text-2xl font-bold text-emerald-600">2</p>
                    <p className="text-xs text-slate-500">Drops</p>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-indigo-100">
                    <p className="text-2xl font-bold text-indigo-600">95%</p>
                    <p className="text-xs text-slate-500">Accurate</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
